import React from 'react'
import { Flex, Grid, Text } from '@radix-ui/themes'
import { isPathAvailable } from 'app/mode-availability'
import { isOnchainUiShown } from 'app/onchain-ui'
import cn from 'classnames'
import { useResponsive } from 'shared/hooks/use-responsive'
import { CARD_MODES, HomeCardType } from '../constants'
import { ModeCard } from './mode-card'
import styles from '../home.module.scss'

const isCardAvailable = (mode: HomeCardType) =>
  !('path' in mode) || !mode.path || isPathAvailable(mode.path)

/** The game cards in the order of the full grid (`.gameModeCards`). */
const GAME_CARDS = [
  { mode: CARD_MODES[5], isWide: true },
  { mode: CARD_MODES[2] },
  { mode: CARD_MODES[4] },
  { mode: CARD_MODES[3], isWide: true },
  { mode: CARD_MODES[9], isNewBadgeShown: true }
]

export const ModeCards: React.FC = () => {
  const [isMobile] = useResponsive(['xs', 'sm'])

  const availableGameCards = GAME_CARDS.filter(card =>
    isCardAvailable(card.mode)
  )
  // NOTE: the full grid places its five cards by position; with modes hidden
  // (`offchainOnly`) the rest (Up/Down, Bull's Eye) share one row instead
  const isFullGameGrid = availableGameCards.length === GAME_CARDS.length
  // NOTE: Airdrop Claim and Staking are on-chain; without them (`offchainOnly`)
  // Rewards, Referral Program and How to play share one row
  const isOnchainUi = isOnchainUiShown()

  return (
    <Flex
      direction={'column'}
      className={styles.cardsSectionsWrapper}
      width={'100%'}
    >
      <Flex
        direction={'column'}
        gap={'1'}
        width={'100%'}
      >
        <Text
          className={cn(styles.sectionTitle, 'color-white')}
          weight={'regular'}
        >
          Game Modes
        </Text>
        {isFullGameGrid ?
          <Grid
            columns={{ initial: '1fr 1fr', sm: '1fr 1fr 1fr' }}
            rows={'1fr 1fr'}
            className={styles.gameModeCards}
            gap={'1'}
          >
            {availableGameCards.map(card => (
              <ModeCard
                key={card.mode.key}
                {...card}
              />
            ))}
          </Grid>
        : <Grid
            columns={{ initial: '1fr', sm: '1fr 1fr' }}
            gap={'1'}
          >
            {availableGameCards.map(card => (
              <ModeCard
                key={card.mode.key}
                mode={card.mode}
                isWide={true}
              />
            ))}
          </Grid>
        }
      </Flex>

      <Flex
        direction={'column'}
        gap={'1'}
        width={'100%'}
      >
        <Text
          className={cn(styles.sectionTitle, 'color-white')}
          weight={'regular'}
        >
          Extra
        </Text>

        {isOnchainUi && (
          <Flex
            align={'stretch'}
            gap={'1'}
            height={'100%'}
          >
            <ModeCard
              mode={CARD_MODES[8]}
              key={CARD_MODES[8].key}
              isNewBadgeShown
            />
          </Flex>
        )}

        <Grid
          columns={
            isOnchainUi ? '1fr 1fr' : { initial: '1fr 1fr', sm: '1fr 1fr 1fr' }
          }
          rows={
            isOnchainUi ? { initial: '1fr 1fr 1fr', sm: '1fr 1fr' } : undefined
          }
          className={styles.extraModeCards}
          gap={'1'}
        >
          <ModeCard
            mode={CARD_MODES[1]}
            key={CARD_MODES[1].key}
            isWide={Boolean(isMobile)}
          />

          <ModeCard
            mode={CARD_MODES[6]}
            key={CARD_MODES[6].key}
            isWide={Boolean(isMobile)}
          />

          {isOnchainUi && (
            <ModeCard
              mode={CARD_MODES[7]}
              key={CARD_MODES[7].key}
              isWide={Boolean(isMobile)}
              isNewBadgeShown
            />
          )}

          <ModeCard
            mode={CARD_MODES[0]}
            key={CARD_MODES[0].key}
          />
        </Grid>
      </Flex>
    </Flex>
  )
}
